import { useEffect, useRef, useState } from 'react'
import { ChevronUp } from 'lucide-react'
import { scrollToTopWithFlight } from './scrollFlight'
import './eagle-back-to-top.css'

const MEDIA = '/media/eagle/'
const EAGLE_QUERY = '(min-width: 768px) and (hover: hover) and (pointer: fine)'

interface EagleBackToTopProps {
  visible: boolean
}

export default function EagleBackToTop({ visible }: EagleBackToTopProps) {
  const [flying, setFlying] = useState(false)
  const [landed, setLanded] = useState(false)
  const [desktopEagle, setDesktopEagle] = useState(() => window.matchMedia(EAGLE_QUERY).matches)
  const [reducedMotion, setReducedMotion] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches)
  const [videoSource, setVideoSource] = useState<string>()
  const [hasAppeared, setHasAppeared] = useState(visible)
  const artRef = useRef<HTMLSpanElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const cancelRef = useRef<(() => void) | null>(null)
  const activeRef = useRef(false)
  const landedRef = useRef(false)

  const resetArt = () => {
    videoRef.current?.pause()
    if (artRef.current) {
      artRef.current.style.transform = ''
      artRef.current.style.opacity = ''
    }
    if (videoRef.current) videoRef.current.style.opacity = '0'
    if (imageRef.current) imageRef.current.style.opacity = '1'
  }

  useEffect(() => {
    const preference = window.matchMedia('(prefers-reduced-motion: reduce)')
    const desktop = window.matchMedia(EAGLE_QUERY)
    const update = () => {
      setReducedMotion(preference.matches)
      if (preference.matches) cancelRef.current?.()
    }
    const updateDesktop = () => {
      setDesktopEagle(desktop.matches)
      if (!desktop.matches) cancelRef.current?.()
    }
    const rearmAfterScrollingDown = () => {
      // A late scroll event from arrival must not reveal the resting pose again.
      if (landedRef.current && window.scrollY > 320) {
        landedRef.current = false
        resetArt()
        setLanded(false)
      }
    }
    preference.addEventListener('change', update)
    desktop.addEventListener('change', updateDesktop)
    window.addEventListener('scroll', rearmAfterScrollingDown, { passive: true })
    return () => {
      preference.removeEventListener('change', update)
      desktop.removeEventListener('change', updateDesktop)
      window.removeEventListener('scroll', rearmAfterScrollingDown)
      activeRef.current = false
      cancelRef.current?.()
      videoRef.current?.pause()
    }
  }, [])

  useEffect(() => {
    if (!desktopEagle) {
      setVideoSource(undefined)
      return
    }
    if (!visible) return
    setHasAppeared(true)
    if (reducedMotion || videoSource) return
    // Some Chromium builds decode HEVC without alpha. Prefer the Apple export only
    // on WebKit, including iOS browsers; elsewhere use the verified VP9 alpha file.
    const ua = navigator.userAgent
    const appleWebKit = /AppleWebKit/.test(ua) && !/Chrome|Chromium|Edg\/|OPR\/|Android/.test(ua)
    const saveData = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData
    if (!saveData) setVideoSource(MEDIA + (appleWebKit ? 'takeoff-hevc.mov' : 'takeoff.webm'))
  }, [visible, reducedMotion, videoSource, desktopEagle])

  const focusStart = () => document.getElementById('main-content')?.focus({ preventScroll: true })

  const launch = () => {
    if (activeRef.current) return
    if (reducedMotion || window.scrollY <= 0) {
      // Override any global CSS smooth scrolling for reduced motion as well.
      const style = document.documentElement.style
      const previous = style.getPropertyValue('scroll-behavior')
      const priority = style.getPropertyPriority('scroll-behavior')
      style.setProperty('scroll-behavior', 'auto', 'important')
      window.scrollTo({ top: 0, behavior: 'auto' })
      landedRef.current = true
      setLanded(true)
      if (previous) style.setProperty('scroll-behavior', previous, priority)
      else style.removeProperty('scroll-behavior')
      focusStart()
      return
    }

    resetArt()
    activeRef.current = true
    setFlying(true)
    const rect = artRef.current?.getBoundingClientRect()
    const travelY = rect ? rect.bottom + 24 : window.innerHeight + 200
    // Move inward as its wings open to keep the full wingspan in the viewport.
    const travelX = rect ? Math.max(0, rect.right - window.innerWidth + 16) : 76

    const finish = (completed: boolean) => {
      const wasActive = activeRef.current
      activeRef.current = false
      cancelRef.current = null
      videoRef.current?.pause()
      if (completed || window.scrollY <= 320) {
        // Leave the art off-screen until the next real scroll down. Resetting it
        // here exposed the perched frame during the wrapper's fade-out.
        landedRef.current = true
        if (wasActive) setLanded(true)
      } else {
        resetArt()
      }
      if (wasActive) {
        setFlying(false)
        if (completed) focusStart()
      }
    }

    cancelRef.current = scrollToTopWithFlight({
      duration: desktopEagle ? 1600 : 450,
      onProgress: (progress, eased) => {
        if (!artRef.current) return
        const inward = Math.min(1, progress / .28)
        const easedInward = inward * inward * (3 - 2 * inward)
        artRef.current.style.transform = `translate3d(${-travelX * easedInward}px, ${-travelY * eased}px, 0)`
        artRef.current.style.opacity = String(Math.min(1, (1 - progress) / .12))
      },
      onComplete: () => finish(true),
      onCancel: () => finish(false),
    })

    // Never hold navigation up for a download, autoplay policy, or decode failure.
    const video = videoRef.current
    if (desktopEagle && video && video.readyState >= 2) {
      try {
        video.currentTime = 0
        const playback = video.play()
        playback?.catch(() => {
          if (videoRef.current) videoRef.current.style.opacity = '0'
          if (imageRef.current) imageRef.current.style.opacity = '1'
        })
      } catch {
        // The still follows the same flight path if playback is unavailable.
      }
    }
  }

  const showVideo = () => {
    if (!activeRef.current) {
      videoRef.current?.pause()
      return
    }
    if (videoRef.current) videoRef.current.style.opacity = '1'
    if (imageRef.current) imageRef.current.style.opacity = '0'
  }

  const hideFailedVideo = () => {
    if (videoRef.current) videoRef.current.style.opacity = '0'
    if (imageRef.current) imageRef.current.style.opacity = '1'
  }

  return (
    <div className="eagle-return-layer">
      <div className={`eagle-return${(visible || flying) && !landed ? ' is-visible' : ''}${flying ? ' is-flying' : ''}${landed ? ' is-landed' : ''}`}>
        {hasAppeared && desktopEagle && (
          <span ref={artRef} className="eagle-return-art" aria-hidden="true">
            <img ref={imageRef} src={MEDIA + 'rest.webp'} width="768" height="426" alt="" decoding="async" draggable={false} />
            {videoSource && !reducedMotion && (
              <video
                ref={videoRef}
                src={videoSource}
                width="768"
                height="426"
                muted
                playsInline
                preload="auto"
                disablePictureInPicture
                onPlaying={showVideo}
                onError={hideFailedVideo}
              />
            )}
          </span>
        )}
        <button
          type="button"
          className="eagle-return-button"
          aria-label="Back to top"
          aria-hidden={!visible || flying || landed}
          tabIndex={visible && !flying && !landed ? 0 : -1}
          disabled={flying}
          onClick={launch}
        >
          <ChevronUp aria-hidden="true" size={20} />
        </button>
      </div>
    </div>
  )
}
